import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_FABRIC_SETTINGS,
  UPDATE_ONE_SETTING,
} from './fabricSettingsQueries'

const LOCALES = ['en', 'da', 'de', 'fr', 'lt'] as const
type Locale = (typeof LOCALES)[number]
type LocaleMap = Record<Locale, string>

const MESSAGE_SETTING_NAMES = [
  'fabric_stock_plenty_message',
  'fabric_stock_low_message',
] as const

const THRESHOLD_SETTING_NAME = 'fabric_low_stock_threshold'

const SETTING_LABELS: Record<string, string> = {
  fabric_stock_plenty_message: 'Stock Plenty Message',
  fabric_stock_low_message: 'Stock Low Message',
  fabric_low_stock_threshold: 'Low Stock Threshold',
}

function parseLocaleMap(value: string | null | undefined): LocaleMap {
  if (!value) return { en: '', da: '', de: '', fr: '', lt: '' }
  try {
    const parsed = JSON.parse(value)
    return {
      en: parsed.en ?? '',
      da: parsed.da ?? '',
      de: parsed.de ?? '',
      fr: parsed.fr ?? '',
      lt: parsed.lt ?? '',
    }
  } catch {
    return { en: '', da: '', de: '', fr: '', lt: '' }
  }
}

interface RawSetting {
  name: string
  title: string
  type: string
  value: string | null
  shopSettingId: number
  description: string
  __typename: string
}

export default function FabricSettings() {
  const { data, loading } = useQuery(GET_FABRIC_SETTINGS)
  const [updateOneSetting] = useMutation(UPDATE_ONE_SETTING)

  // State for message locale maps
  const [messageMaps, setMessageMaps] = useState<
    Record<string, LocaleMap>
  >({
    fabric_stock_plenty_message: { en: '', da: '', de: '', fr: '', lt: '' },
    fabric_stock_low_message: { en: '', da: '', de: '', fr: '', lt: '' },
  })

  // State for threshold (stored as string)
  const [threshold, setThreshold] = useState<string>('')

  // Track which settings have been changed
  const [dirtySettings, setDirtySettings] = useState<Set<string>>(new Set())

  const [isSaving, setIsSaving] = useState(false)

  // Initialize form from fetched data
  useEffect(() => {
    if (!data?.findManySetting) return

    const settings = data.findManySetting as RawSetting[]

    const newMessageMaps = { ...messageMaps }
    for (const name of MESSAGE_SETTING_NAMES) {
      const found = settings.find(s => s.name === name)
      newMessageMaps[name] = parseLocaleMap(found?.value)
    }
    setMessageMaps(newMessageMaps)

    const thresholdSetting = settings.find(
      s => s.name === THRESHOLD_SETTING_NAME
    )
    setThreshold(thresholdSetting?.value ?? '')

    setDirtySettings(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleLocaleChange = (
    settingName: string,
    locale: Locale,
    text: string
  ) => {
    setMessageMaps(prev => ({
      ...prev,
      [settingName]: {
        ...prev[settingName],
        [locale]: text,
      },
    }))
    setDirtySettings(prev => new Set(prev).add(settingName))
  }

  const handleThresholdChange = (value: string) => {
    setThreshold(value)
    setDirtySettings(prev => new Set(prev).add(THRESHOLD_SETTING_NAME))
  }

  const handleSave = async () => {
    if (dirtySettings.size === 0) {
      toast.info('No changes to save.')
      return
    }

    setIsSaving(true)
    const errors: string[] = []

    for (const settingName of dirtySettings) {
      let newValue: string

      if (
        (MESSAGE_SETTING_NAMES as readonly string[]).includes(settingName)
      ) {
        newValue = JSON.stringify(messageMaps[settingName])
      } else {
        newValue = threshold
      }

      try {
        await updateOneSetting({
          variables: {
            data: {
              value: { set: newValue },
            },
            where: {
              name: settingName,
            },
          },
        })
      } catch (err) {
        console.error(`Failed to update setting "${settingName}":`, err)
        errors.push(SETTING_LABELS[settingName] ?? settingName)
      }
    }

    setIsSaving(false)

    if (errors.length > 0) {
      toast.error(`Failed to save: ${errors.join(', ')}`)
    } else {
      toast.success('Fabric settings saved successfully.')
      setDirtySettings(new Set())
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex justify-center items-center h-64">
          <div>Loading...</div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center mb-8">
        <HeaderSection
          title="Fabric Settings"
          subtitle="Configure stock messages and threshold for fabric availability."
        />
      </div>

      <div className="max-w-[1000px] space-y-6">
        {/* Message settings */}
        {MESSAGE_SETTING_NAMES.map(settingName => (
          <Card key={settingName} className="p-6">
            <h3 className="text-base font-semibold mb-1">
              {SETTING_LABELS[settingName]}
            </h3>
            <p className="text-xs text-gray-500 mb-4">{settingName}</p>
            <div className="space-y-4">
              {LOCALES.map(locale => (
                <div key={locale}>
                  <Label
                    htmlFor={`${settingName}-${locale}`}
                    className="mb-1 block text-sm font-medium uppercase tracking-wide text-gray-600"
                  >
                    {locale}
                  </Label>
                  <Textarea
                    id={`${settingName}-${locale}`}
                    value={messageMaps[settingName][locale]}
                    onChange={e =>
                      handleLocaleChange(settingName, locale, e.target.value)
                    }
                    rows={2}
                    placeholder={`Message in ${locale}`}
                  />
                </div>
              ))}
            </div>
          </Card>
        ))}

        {/* Threshold setting */}
        <Card className="p-6">
          <h3 className="text-base font-semibold mb-1">
            {SETTING_LABELS[THRESHOLD_SETTING_NAME]}
          </h3>
          <p className="text-xs text-gray-500 mb-4">{THRESHOLD_SETTING_NAME}</p>
          <div className="max-w-[200px]">
            <Label
              htmlFor="fabric-low-stock-threshold"
              className="mb-1 block text-sm font-medium text-gray-600"
            >
              Threshold value
            </Label>
            <Input
              id="fabric-low-stock-threshold"
              type="number"
              value={threshold}
              onChange={e => handleThresholdChange(e.target.value)}
              min={0}
              placeholder="e.g. 50"
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </PageContent>
  )
}
